import { HTTP_INTERCEPTORS, HttpClient, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { APP_INITIALIZER, importProvidersFrom } from '@angular/core';
import { AngularFireModule } from '@angular/fire/compat';
import { AngularFireAuthModule } from '@angular/fire/compat/auth';
import { AngularFirestoreModule } from '@angular/fire/compat/firestore';
import { ApplicationConfig } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter } from '@angular/router';
import { ServiceWorkerModule } from '@angular/service-worker';
import { MSAL_INSTANCE, MSAL_INTERCEPTOR_CONFIG, MsalInterceptor, MsalInterceptorConfiguration, MsalModule, MsalService } from '@azure/msal-angular';
import { InteractionType, IPublicClientApplication, PublicClientApplication } from '@azure/msal-browser';
import { environment } from '@environments/environment';
import { NgIdleKeepaliveModule } from '@ng-idle/keepalive';
import { ModalModule } from 'ngx-bootstrap/modal';
import { CookieService } from 'ngx-cookie-service';
import { JoyrideModule } from 'ngx-joyride';
import { ToastrModule } from 'ngx-toastr';
import { NgxUiLoaderModule } from 'ngx-ui-loader';
import { NgMultiSelectDropDownModule } from 'ng-multiselect-dropdown';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';

import { APP_ROUTES } from './app.routes';
import { ToolbarService } from './features/student/canvas-dashboard/services/toolbar.service';
import { FileManagerService } from './features/student/services/file-manager.service';
import { AdminRouteGuard } from './shared/helper/admin-route.guard';
import { GoogleAccessTokenGuard } from './shared/helper/google-access-token-guard';
import { RouteGuard } from './shared/helper/route.guard';
import { SiteAdminSessionGuard } from './shared/helper/site-admin-session-guard';
import { SubscriptionExpirationGuard } from './shared/helper/subscription-expiration.guard';
import { UserAuthGuard } from './shared/helper/user-auth.guard';
import { Interceptor, LoaderService } from './shared/services';
import { provideOAuthClient } from 'angular-oauth2-oidc';
import { AssignmentService } from './features/student/services/assignment.service';

export function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication(environment.Azure.clientapp);
}

export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http, './assets/i18n/', '.json');
}

export function MSALInstanceConfigFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<string, Array<string>>();
  protectedResourceMap.set(environment.Azure.GetProfile, ['user.read']);
  return {
    interactionType: InteractionType.Popup,
    protectedResourceMap,
  };
}

// TEMPORARY (geometry font test): load the bundled geometry families before bootstrap finishes, so
// they are ready by the time the dashboard route builds the CreateJS stage. The 32px is only a
// trigger size and does not limit the sizes CreateJS can ask for.
export function GeometryFontsFactory() {
  return () =>
    Promise.all([document.fonts.load('400 32px "KiwiGeometryNoto"'), document.fonts.load('400 32px "KiwiGeometryDejaVu"')]).catch(err =>
      console.error('[geometry-font-test] font load failed:', err),
    );
}

export const provideTranslation = () => ({
  loader: {
    provide: TranslateLoader,
    useFactory: HttpLoaderFactory,
    deps: [HttpClient],
  },
});

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(APP_ROUTES),
    provideAnimations(),
    provideHttpClient(withInterceptorsFromDi()),
    provideOAuthClient(),
    importProvidersFrom(
      ToastrModule.forRoot({
        preventDuplicates: true,
        maxOpened: 1,
        timeOut: 4000,
      }),
      NgxUiLoaderModule.forRoot(LoaderService.ngxUiLoaderConfig),
      AngularFireModule.initializeApp(environment.firebase),
      AngularFireAuthModule,
      AngularFirestoreModule,
      NgIdleKeepaliveModule.forRoot(),
      ModalModule.forRoot(),
      JoyrideModule.forRoot(),
      NgMultiSelectDropDownModule.forRoot(),
      MsalModule,
      ServiceWorkerModule.register('ngsw-worker.js', { enabled: environment.serviceWorkerEnabled }),
      TranslateModule.forRoot(provideTranslation()),
    ),
    CookieService,
    RouteGuard,
    AdminRouteGuard,
    UserAuthGuard,
    GoogleAccessTokenGuard,
    SubscriptionExpirationGuard,
    SiteAdminSessionGuard,
    ToolbarService,
    FileManagerService,
    AssignmentService,
    MsalService,
    { provide: APP_INITIALIZER, useFactory: GeometryFontsFactory, multi: true },
    { provide: MSAL_INSTANCE, useFactory: MSALInstanceFactory },
    { provide: HTTP_INTERCEPTORS, useClass: Interceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: MsalInterceptor, multi: true },
    { provide: MSAL_INTERCEPTOR_CONFIG, useFactory: MSALInstanceConfigFactory },
  ],
};
